import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useApi } from '../../contexts/ApiContext';
import {
  getAgentStatusLabel,
  getAgentStatusColor
} from '../../constants/agentStatus';

const AgentDetailsModal = ({ open, onClose, agentId, agentName, date }) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (open && agentId) {
      fetchAgentStats();
    }
  }, [open, agentId, date]);

  const fetchAgentStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = date ? { date } : {};
      const response = await api.agents.getDailyStats(agentId, params);
      if (response.success) {
        console.log('[AgentDetails] Stats received:', response.data);
        console.log('[AgentDetails] Connection time:', response.data?.statusSummary?.total_connection_time);
        setStats(response.data);
      } else {
        setError(response.message || 'Error al cargar estadísticas');
      }
    } catch (err) {
      setError('Error al cargar estadísticas del agente');
      console.error('Error fetching agent stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon />
          <Typography variant="h6">
            Detalles del Agente: {agentName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && stats && (
          <Box>
            {/* KPI Cards - Métricas Principales */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Llamadas Atendidas
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {stats.callStats.total_calls}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Tiempo en Llamada
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {formatDuration(stats.callStats.total_call_duration)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Promedio ACW
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {formatDuration(stats.callStats.avg_acw_duration)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Desconexiones
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {stats.connectionStats.disconnection_count}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Sección: Conexión */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ScheduleIcon color="primary" />
                  <Typography variant="h6">Conexión</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Primera Conexión
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDateTime(stats.connectionStats.first_connection)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Última Desconexión
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDateTime(stats.connectionStats.last_disconnection)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Desconexiones
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {stats.connectionStats.disconnection_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Tiempo Conectado
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDuration(stats.statusSummary.total_connection_time)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Sección: Tiempo por Estado */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" mb={2}>
                  Tiempo por Estado
                </Typography>
                <Paper elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Veces</TableCell>
                        <TableCell align="right">Tiempo Total</TableCell>
                        <TableCell align="right">Promedio</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.statusSummary.by_status.map((status) => (
                        <TableRow key={status.status} hover>
                          <TableCell>
                            <Chip
                              label={getAgentStatusLabel(status.status)}
                              size="small"
                              sx={{
                                backgroundColor: getAgentStatusColor(status.status),
                                color: 'white',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{status.count}</TableCell>
                          <TableCell align="right">
                            {formatDuration(status.total_duration)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDuration(status.avg_duration)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </CardContent>
            </Card>

            {/* Sección: Estadísticas de Llamadas */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PhoneIcon color="primary" />
                  <Typography variant="h6">Estadísticas de Llamadas</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Colgadas por Agente
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {stats.callStats.hangup_by_agent}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Colgadas por Usuario
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {stats.callStats.hangup_by_user}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Promedio en Llamada
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {formatDuration(stats.callStats.avg_call_duration)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Promedio Cola
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {formatDuration(stats.callStats.avg_queue_duration)}
                    </Typography>
                  </Grid>
                  {stats.callStats.avg_preview_duration > 0 && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Promedio Preview
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {formatDuration(stats.callStats.avg_preview_duration)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Sección: Tipificaciones */}
            {stats.typifications && stats.typifications.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <AssessmentIcon color="primary" />
                    <Typography variant="h6">Tipificaciones</Typography>
                  </Box>
                  <Paper elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                          <TableCell>Tipificación</TableCell>
                          <TableCell align="right">Cantidad</TableCell>
                          <TableCell align="right">Duración Promedio</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.typifications.map((typ, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{typ.typification}</TableCell>
                            <TableCell align="right">{typ.count}</TableCell>
                            <TableCell align="right">
                              {formatDuration(typ.avg_duration)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentDetailsModal;
